// Passo a passo de instalação da tag por plataforma. Linguagem simples: o público não é técnico.
// Os nomes de menu mudam de vez em quando nas plataformas; por isso cada guia tem um "se não encontrar".
import type { SitePlatform } from '@impulso/shared/api/tracking'

export interface PlatformGuide {
  id: SitePlatform
  name: string
  /** Letra/abreviação do selo */
  mark: string
  color: string
  description: string
  steps: string[]
  tips: string[]
  /** Link oficial de ajuda da plataforma */
  helpUrl?: string
}

export const PLATFORM_GUIDES: PlatformGuide[] = [
  {
    id: 'wordpress',
    name: 'WordPress',
    mark: 'W',
    color: '#21759b',
    description: 'Sites em WordPress, com ou sem Elementor.',
    steps: [
      'Entre no painel do WordPress (seusite.com.br/wp-admin).',
      'Vá em Plugins → Adicionar novo, procure por "WPCode" e clique em Instalar e depois em Ativar.',
      'No menu da esquerda, abra Code Snippets → Header & Footer.',
      'Cole o código do Seu Impulso no campo "Header" e clique em "Save Changes" (Salvar).',
    ],
    tips: [
      'Usa Elementor Pro? Dá para colar em Elementor → Código personalizado, com local "<head>".',
      'Se o site tem plugin de cache (WP Rocket, LiteSpeed, W3 Total Cache), limpe o cache depois de salvar.',
    ],
    helpUrl: 'https://wordpress.org/plugins/insert-headers-and-footers/',
  },
  {
    id: 'wix',
    name: 'Wix',
    mark: 'Wx',
    color: '#0c6efc',
    description: 'Sites feitos no Wix (precisa de plano Premium com domínio).',
    steps: [
      'No painel do seu site Wix, abra Configurações.',
      'Role até "Avançado" e clique em "Código personalizado".',
      'Clique em "+ Adicionar código personalizado" e cole o código do Seu Impulso.',
      'Dê o nome "Seu Impulso", escolha "Todas as páginas" → "Carregar o código uma vez" e o local "Head".',
      'Clique em Aplicar. Pronto, não precisa publicar de novo.',
    ],
    tips: [
      'O Wix só libera código personalizado em sites Premium com domínio conectado.',
      'Se você já ligou o Pixel da Meta pelas integrações do Wix, deixe o campo do pixel vazio aqui para não contar em dobro.',
    ],
  },
  {
    id: 'shopify',
    name: 'Shopify',
    mark: 'S',
    color: '#5e8e3e',
    description: 'Lojas Shopify.',
    steps: [
      'No admin da Shopify, vá em Loja virtual → Temas.',
      'No tema atual, clique nos três pontinhos (…) → Editar código.',
      'Abra o arquivo layout/theme.liquid.',
      'Cole o código do Seu Impulso logo antes da linha </head> e clique em Salvar.',
    ],
    tips: [
      'O checkout da Shopify não carrega código do tema. Para medir compras, use também os apps oficiais da Meta e do Google na Shopify.',
    ],
  },
  {
    id: 'nuvemshop',
    name: 'Nuvemshop',
    mark: 'N',
    color: '#2c3357',
    description: 'Lojas na Nuvemshop.',
    steps: [
      'No administrador da Nuvemshop, vá em Configurações → Códigos externos.',
      'Procure o campo de códigos de rastreamento (para todas as páginas).',
      'Cole o código do Seu Impulso e clique em Salvar.',
    ],
    tips: [
      'A Nuvemshop também tem campos próprios para o Pixel da Meta e o Google Analytics. Se já usa, deixe esses IDs vazios aqui e use o Seu Impulso para medir os cliques no WhatsApp.',
    ],
  },
  {
    id: 'gtm',
    name: 'Google Tag Manager',
    mark: 'GTM',
    color: '#246fdb',
    description: 'Se o seu site já usa o Gerenciador de tags do Google.',
    steps: [
      'Entre em tagmanager.google.com e abra o contêiner do seu site.',
      'Clique em Tags → Nova → Configuração da tag → "HTML personalizado".',
      'Cole o código do Seu Impulso.',
      'Em Acionamento, escolha "All Pages" (todas as páginas) e salve.',
      'Clique em Enviar → Publicar.',
    ],
    tips: [
      'Instalado pelo GTM, o código não aparece no HTML do site. Por isso a confirmação vem pelos sinais recebidos (visite o site depois de publicar).',
    ],
  },
  {
    id: 'custom',
    name: 'Hospedagem própria',
    mark: '</>',
    color: '#374151',
    description: 'Site em HTML, PHP ou feito por um programador.',
    steps: [
      'Abra o arquivo que monta o topo de todas as páginas (ex.: header.php, index.html ou o layout principal).',
      'Cole o código do Seu Impulso logo antes da linha </head>.',
      'Salve e envie o arquivo para a hospedagem (pelo painel, FTP ou como seu site costuma ser publicado).',
    ],
    tips: [
      'Se cada página tem o próprio arquivo, cole em todas, ou melhor, no modelo que elas compartilham.',
      'Não sabe mexer nisso? Use "Mandar para quem cuida do site" logo abaixo.',
    ],
  },
  {
    id: 'other',
    name: 'Outra / não sei',
    mark: '?',
    color: '#9ca3af',
    description: 'Outra plataforma ou você não sabe como o site foi feito.',
    steps: [
      'Procure nas configurações do seu site uma opção chamada "código personalizado", "scripts", "head" ou "rastreamento".',
      'Cole o código do Seu Impulso nesse campo, de forma que ele apareça em todas as páginas.',
      'Se não encontrar, mande as instruções para quem fez o site. Leva 5 minutos para essa pessoa.',
    ],
    tips: ['Também dá para trocar o site por uma landing page feita aqui, já com tudo medido.'],
  },
]

export const guideFor = (id: SitePlatform | null | undefined) => PLATFORM_GUIDES.find((g) => g.id === id) ?? null

/** Mensagem pronta para mandar ao programador/agência. */
export function developerMessage(companyName: string, snippet: string, siteUrl: string | null): string {
  return [
    `Oi! Preciso instalar o código de rastreamento de anúncios do Seu Impulso no site da ${companyName}${siteUrl ? ` (${siteUrl})` : ''}.`,
    '',
    'É só colar este código dentro do <head> de TODAS as páginas (de preferência no modelo/tema compartilhado):',
    '',
    snippet,
    '',
    'Ele carrega o Pixel da Meta e a tag do Google configurados na nossa conta e mede cliques no WhatsApp, em telefones e o envio de formulários. Não altera nada visualmente no site.',
    'Depois de publicar, me avisa que eu confirmo pelo painel. Obrigado!',
  ].join('\n')
}
